import { useState, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { useEngineerPhoto } from '../../hooks/useEngineerPhoto';
import { readAsDataUrl, shrinkImage } from '../../utils/shrinkImage';
import { Toast } from '../ui/Toast';
import { DISCIPLINES, DEFAULT_DISCIPLINE, disciplineOf } from '../../data/disciplines';
import { resourceGroupLabel, resourceGroupOf, showsOnCards } from '../../data/resourceGroups';

const STATUSES = ['On-Site', 'Office', 'PTO'];
// Where someone works. "1099" used to live here too, which meant a contractor
// had no region at all - that is `employment_type` now, and 1099 only stays
// selectable so existing rows are not silently re-assigned.
const REGIONS = ['NA', 'SA', 'EU', 'MEA'];
const REGION_LABEL = {
  NA: 'North America (NA)',
  SA: 'South Africa (SA)',
  EU: 'Europe (EU)',
  MEA: 'Middle East (MEA)',
  1099: '1099 (legacy)',
};
// Interns can be scheduled on the calendar like anyone else; utilization and
// the forecast's capacity leave them out (utils/utilizationFromCalendar).
const EMPLOYMENT = ['Employee', 'Contractor', 'Intern'];
const employmentOf = (v) =>
  EMPLOYMENT.find(
    (t) =>
      t.toLowerCase() ===
      String(v || '')
        .trim()
        .toLowerCase()
  ) || 'Employee';

const LABEL = { fontSize: 10.5, fontWeight: 700, color: '#666', marginBottom: 4, display: 'block' };
const INPUT = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: 6,
  border: '1px solid #E0E0E0',
  fontSize: 11,
  fontFamily: 'Montserrat',
  outline: 'none',
  boxSizing: 'border-box',
};
const REQUIRED = { color: '#C62828', marginLeft: 2 };

const blank = {
  name: '',
  title: '',
  region: 'NA',
  employmentType: 'Employee',
  skill: '',
  discipline: DEFAULT_DISCIPLINE,
  location: '',
  status: 'Office',
  globalProjectNumber: '',
  photoUrl: '',
  notes: '',
};

export default function EngineerForm({ onClose, initialData }) {
  const { addEngineer, updateEngineer } = useApp();
  const isEdit = !!initialData;

  const [f, setF] = useState(
    isEdit
      ? {
          name: initialData.name || '',
          title: initialData.title || '',
          region: initialData.region || 'NA',
          employmentType: employmentOf(initialData.employmentType || initialData.employment_type),
          skill: initialData.skill || '',
          discipline: disciplineOf(initialData.discipline),
          location: initialData.location || '',
          status: initialData.status || 'Office',
          globalProjectNumber: initialData.globalProjectNumber || initialData.global_project_number || '',
          // undefined = the photo is left as it is. The list carries no photo bytes
          // (10/13/26), so the form must never send the photo back unless it was
          // changed here: a picked picture (shrunk), or '' for Remove photo.
          photoUrl: undefined,
          notes: initialData.notes || '',
        }
      : blank
  );
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(false);

  const set = (k) => (e) => {
    setF((p) => ({ ...p, [k]: e.target.value }));
    if (errors[k])
      setErrors((p) => {
        const n = { ...p };
        delete n[k];
        return n;
      });
  };

  // The saved picture, for the preview while it is not being changed here.
  const savedPhoto = useEngineerPhoto(isEdit ? initialData : null);

  // A picked picture is made a 400 px JPEG before it is kept (a phone photo
  // was stored at ~1.4 MB, and every one was in the roster list). If the
  // browser cannot shrink it, the picture is kept as it is.
  const [photoBusy, setPhotoBusy] = useState(false);
  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoBusy(true);
    try {
      let url;
      try {
        url = await shrinkImage(file);
      } catch {
        url = await readAsDataUrl(file);
      }
      setF((p) => ({ ...p, photoUrl: url }));
    } catch {
      /* unreadable file: nothing changes */
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleSave = useCallback(() => {
    const errs = {};
    if (!f.name.trim()) errs.name = true;
    if (!f.title.trim()) errs.title = true;
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const initials = f.name
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase();
    const data = {
      initials,
      name: f.name,
      title: f.title,
      region: f.region,
      employmentType: f.employmentType,
      skill: f.skill || '-',
      discipline: f.discipline,
      location: f.location || '-',
      status: f.status,
      // Replaces "Hours This Week" (revisions 2, 10/2/26). Optional: the
      // number is not always issued when someone is first put on a job. The
      // calendar also writes it here from the block they are on today.
      globalProjectNumber: f.globalProjectNumber.trim(),
      notes: f.notes || '',
    };
    // only a photo changed here is sent (see the form state above)
    if (!isEdit) data.photoUrl = f.photoUrl || '';
    else if (f.photoUrl !== undefined) data.photoUrl = f.photoUrl;

    if (isEdit) updateEngineer(initialData.id, data);
    else addEngineer(data);
    setToast(true);
    setTimeout(() => onClose(), 1200);
  }, [f, isEdit, initialData, addEngineer, updateEngineer, onClose]);

  const errBorder = (k) => (errors[k] ? { border: '1px solid #C62828' } : {});

  const photoPreview = f.photoUrl !== undefined ? f.photoUrl || null : savedPhoto || null;
  const initials = f.name
    ? f.name
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase()
    : '?';

  return (
    <div>
      {toast && <Toast message={isEdit ? 'Engineer updated' : 'Engineer added'} onDone={() => setToast(false)} />}

      <div style={{ padding: '24px 28px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#1B5E20', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
          Engineer Information
        </div>

        {/* Photo section */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 10,
              overflow: 'hidden',
              background: '#0D0D0D',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {photoPreview ? (
              <img src={photoPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ color: '#43A047', fontSize: 16, fontWeight: 800 }}>{initials}</span>
            )}
          </div>
          <div>
            <label style={{ ...LABEL, marginBottom: 6 }}>Profile Photo</label>
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              disabled={photoBusy}
              style={{ fontSize: 10, fontFamily: 'Montserrat' }}
            />
            {photoPreview && (
              <button
                onClick={() => setF((p) => ({ ...p, photoUrl: '' }))}
                style={{
                  fontSize: 9,
                  color: '#B71C1C',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  marginTop: 4,
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                }}
              >
                Remove photo
              </button>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 24px' }}>
          <div>
            <label style={LABEL}>
              Full Name<span style={REQUIRED}>*</span>
            </label>
            <input style={{ ...INPUT, ...errBorder('name') }} value={f.name} onChange={set('name')} placeholder="e.g. John Smith" />
            {errors.name && <span style={{ fontSize: 9, color: '#C62828', marginTop: 2, display: 'block' }}>Name is required</span>}
          </div>
          <div>
            <label style={LABEL}>
              Title<span style={REQUIRED}>*</span>
            </label>
            <input
              style={{ ...INPUT, ...errBorder('title') }}
              value={f.title}
              onChange={set('title')}
              placeholder="e.g. Controls Engineer"
            />
            {errors.title && <span style={{ fontSize: 9, color: '#C62828', marginTop: 2, display: 'block' }}>Title is required</span>}
          </div>
          <div>
            <label style={LABEL}>Region</label>
            <select style={INPUT} value={f.region} onChange={set('region')}>
              {[...REGIONS, ...(f.region && !REGIONS.includes(f.region) ? [f.region] : [])].map((r) => (
                <option key={r} value={r}>
                  {REGION_LABEL[r] || r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={LABEL}>Employment</label>
            <select style={INPUT} value={f.employmentType} onChange={set('employmentType')}>
              {EMPLOYMENT.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          {/* Region + Employment decide where this person is listed
              (data/resourceGroups.js) - said here so nobody has to guess. */}
          <div
            data-testid="engineer-group-hint"
            style={{ gridColumn: '1 / -1', marginTop: -6, fontSize: 9.5, color: '#888', fontWeight: 600 }}
          >
            {(() => {
              const g = resourceGroupOf({ region: f.region, employmentType: f.employmentType });
              return `Listed under ${resourceGroupLabel(g)}: ${showsOnCards({ region: f.region, employmentType: f.employmentType }) ? 'gets an engineer card, and is on Resources' : 'on Resources only, no engineer card'}.`;
            })()}
          </div>
          <div>
            {/* Picking PTO here clears nothing: going PTO and back used to
                wipe the saved Location and Project #. The calendar sync blanks
                them on the actual PTO days (server/lib/calendarCards). */}
            <label style={LABEL}>Status</label>
            <select style={INPUT} value={f.status} onChange={set('status')}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={LABEL}>Skill / Specialty</label>
            <input style={INPUT} value={f.skill} onChange={set('skill')} placeholder="e.g. P-Soft / Controls" />
          </div>
          <div>
            {/* Replaces the old free-text "Current Project" box. What someone
                is working on is the calendar's answer, not a field you retype
                here and hope stays true. */}
            <label style={LABEL}>Discipline</label>
            <select style={INPUT} value={f.discipline} onChange={set('discipline')}>
              {DISCIPLINES.map((d) => (
                <option key={d.key} value={d.key}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={LABEL}>Location</label>
            <input style={INPUT} value={f.location} onChange={set('location')} placeholder="e.g. Auburn Hills, MI" />
          </div>
          <div>
            <label style={LABEL} htmlFor="engineer-gpn">
              Global Project Number
            </label>
            <input
              id="engineer-gpn"
              style={INPUT}
              value={f.globalProjectNumber}
              onChange={set('globalProjectNumber')}
              placeholder="Optional - not always issued yet"
            />
          </div>
        </div>

        {/* Notes */}
        <div style={{ marginTop: 14 }}>
          <label style={LABEL}>Notes</label>
          <textarea
            style={{ ...INPUT, minHeight: 60, resize: 'vertical' }}
            value={f.notes}
            onChange={set('notes')}
            placeholder="Internal notes about this engineer..."
          />
        </div>
      </div>

      {/* FOOTER */}
      <div
        style={{
          padding: '16px 24px',
          borderTop: '1px solid #F0F0F0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 10,
          flexShrink: 0,
        }}
      >
        <button
          onClick={onClose}
          style={{
            background: '#fff',
            color: '#555',
            border: '1px solid #E0E0E0',
            padding: '9px 20px',
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 11,
            cursor: 'pointer',
            fontFamily: 'Montserrat',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={photoBusy}
          style={{
            background: '#1B5E20',
            color: '#fff',
            border: 'none',
            padding: '9px 20px',
            borderRadius: 6,
            fontWeight: 700,
            fontSize: 11,
            cursor: 'pointer',
            fontFamily: 'Montserrat',
          }}
        >
          {isEdit ? 'Update Engineer' : 'Add Engineer'}
        </button>
      </div>
    </div>
  );
}
